import { useMemo } from "react";
import { useUser } from "@/features/auth/store";
import { isSuperAdmin } from "@/lib/auth-utils";
import type { Department } from "@/types/department";
import type { DepartmentAccessDto } from "@/types/api";

function accessIds(
  access: DepartmentAccessDto | undefined,
  user: {
    departmentId?: string | null;
    subAdminDepartmentIds?: string[];
    departmentIds?: string[];
  } | null | undefined,
): string[] {
  if (!user) return [];
  if (access?.type === "ALL") return [];
  if (access?.type === "RESTRICTED") return access.departmentIds;
  return getUserDepartmentIdsLegacy(user);
}

function cmsAccessIds(
  user: {
    cmsDepartmentAccess?: DepartmentAccessDto;
    departmentId?: string | null;
    subAdminDepartmentIds?: string[];
    departmentIds?: string[];
  } | null | undefined,
): string[] {
  if (!user) return [];
  return accessIds(user.cmsDepartmentAccess, user);
}

function governanceAccessIds(
  user: {
    governanceDepartmentAccess?: DepartmentAccessDto;
    departmentId?: string | null;
    subAdminDepartmentIds?: string[];
    departmentIds?: string[];
  } | null | undefined,
): string[] {
  if (!user) return [];
  return accessIds(user.governanceDepartmentAccess, user);
}

/** True when the signed-in user is limited to specific departments (Editor). */
export function useIsDepartmentRestricted(): boolean {
  const user = useUser();
  return !isSuperAdmin(user) && user?.cmsDepartmentAccess?.type !== "ALL";
}

/** @deprecated Prefer `cmsDepartmentAccess` from /api/auth/me */
export function getUserDepartmentIdsLegacy(
  user: {
    departmentId?: string | null;
    subAdminDepartmentIds?: string[];
    departmentIds?: string[];
  } | null | undefined,
): string[] {
  if (!user) return [];
  if (user.departmentIds?.length) return user.departmentIds;
  const ids = new Set<string>();
  if (user.departmentId) ids.add(user.departmentId);
  for (const id of user.subAdminDepartmentIds ?? []) ids.add(id);
  return [...ids];
}

export function getUserDepartmentIds(
  user: {
    cmsDepartmentAccess?: DepartmentAccessDto;
    departmentId?: string | null;
    subAdminDepartmentIds?: string[];
    departmentIds?: string[];
  } | null | undefined,
): string[] {
  return cmsAccessIds(user);
}

/** Allowed department documentIds for CMS/content (empty when unrestricted). */
export function useAllowedDepartmentIds(): string[] {
  const user = useUser();
  if (user?.cmsDepartmentAccess?.type === "ALL") return [];
  return getUserDepartmentIds(user);
}

/** Departments visible in CMS/content filters — uses `cmsDepartmentAccess`. */
export function useAllowedDepartments(departments: Department[]): Department[] {
  const allowedIds = useAllowedDepartmentIds();
  const user = useUser();

  return useMemo(() => {
    if (user?.cmsDepartmentAccess?.type === "ALL") return departments;
    if (allowedIds.length === 0) return [];
    return departments.filter((d) => allowedIds.includes(d.id));
  }, [allowedIds, departments, user]);
}

/** Allowed department documentIds for governance screens (empty when unrestricted). */
export function useGovernanceDepartmentIds(): string[] {
  const user = useUser();
  if (isSuperAdmin(user)) return [];
  if (user?.governanceDepartmentAccess?.type === "ALL") return [];
  return governanceAccessIds(user);
}

/** Departments visible in users/reports/audit/documents — uses `governanceDepartmentAccess`. */
export function useGovernanceAllowedDepartments(
  departments: Department[],
): Department[] {
  const allowedIds = useGovernanceDepartmentIds();
  const user = useUser();

  return useMemo(() => {
    if (isSuperAdmin(user)) return departments;
    if (user?.governanceDepartmentAccess?.type === "ALL") return departments;
    if (allowedIds.length === 0) return [];
    return departments.filter((d) => allowedIds.includes(d.id));
  }, [allowedIds, departments, user]);
}
